import React, { useState, useEffect } from "react";
import axios from "axios";
import { auth, db } from "../../firebase";
import { doc, setDoc } from "firebase/firestore";
import styleClasses from "./css/MyPage.module.scss";

// API 엔드포인트
const API_ACTIVITY_KOPIS_URL = "https://music-api-134893551192.asia-northeast3.run.app/api/activity/kopis";
const API_ACTIVITY_KCISA_URL = "https://music-api-134893551192.asia-northeast3.run.app/api/activity/kcisa";

function ImportStages() {
  // KOPIS 관련 상태
  const [stdate, setStdate] = useState(""); // 공연시작일 (YYYYMMDD)
  const [eddate, setEddate] = useState("");   // 공연종료일 (YYYYMMDD)
  const [shprfnm, setShprfnm] = useState("");   // 공연명
  const [kopisActivities, setKopisActivities] = useState([]);

  // KCISA 관련 상태 (공연분류값와 공연명 검색값)
  const [kcisaCategory, setKcisaCategory] = useState(""); // 예: "뮤지컬", "연극", ...
  const [kcisaTitle, setKcisaTitle] = useState("");         // 검색값 (공연명)
  const [kcisaActivities, setKcisaActivities] = useState([]);

  const [loading, setLoading] = useState(false);
  const [uid, setUid] = useState(null);
  const [authToken, setAuthToken] = useState("");
  const striptags = require("striptags");
  const he = require("he");

  // Firebase 로그인 상태 감지 및 토큰 저장
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUid(user.uid);
        const token = await user.getIdToken();
        setAuthToken(token);
      }
    });
    return () => unsubscribe();
  }, []);

  // KOPIS 활동 데이터를 가져오는 함수
  const fetchKopisActivities = async () => {
    if (!stdate || !eddate || !shprfnm) {
      alert("공연시작일, 공연종료일, 공연명을 모두 입력하세요 (KOPIS).");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(API_ACTIVITY_KOPIS_URL, {
        params: { stdate, eddate, shprfnm },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      console.log("🎶 KOPIS API 응답 데이터:", response.data);
      const mapped = response.data.activities.map((perf) => ({
        id: perf.id,
        title: perf.title,
        date: perf.date, // 이미 원하는 형식(예: "YYYY.MM.DD")일 수 있으므로 필요시 변환 적용
        channel: perf.channel,
        photo: perf.photo,
        description: perf.description || ""
      }));
      setKopisActivities(mapped);
    } catch (error) {
      console.error("❌ KOPIS 활동 API 호출 실패:", error.response?.data || error.message);
      alert("KOPIS 활동 검색 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // KCISA 활동 데이터를 가져오는 함수
  const fetchKcisaActivities = async () => {
    if (!kcisaCategory || !kcisaTitle) {
      alert("공연분류값와 공연명을 모두 입력하세요 (KCISA).");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(API_ACTIVITY_KCISA_URL, {
        params: { dtype: kcisaCategory, title: kcisaTitle },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      console.log("🎶 KCISA API 응답 데이터:", response.data);

      let items = response.data.activities;
      if (!items) {
        items = [];
      }
      if (!Array.isArray(items)) {
        items = [items];
      }
      const mapped = items.map((item, index) => ({
        id: item.id || `${item.title}_${index}`, // id가 없으면 생성
        title: item.title ? he.decode(striptags(item.title)) : "",
        date: item.date 
        ? `${item.date.slice(0, 4)}-${item.date.slice(4, 6)}-${item.date.slice(6, 8)}` 
        : "",
        channel: item.channel || "",
        description: item.description ? he.decode(striptags(item.description)) : "",
        link: item.link || "",
        photo: item.photo || ""
      }));
      setKcisaActivities(mapped);
    } catch (error) {
      console.error("❌ KCISA 활동 API 호출 실패:", error.response?.data || error.message);
      alert("KCISA 활동 검색 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // Firestore에 활동 정보를 저장 (공통 함수)
  // 저장 시, channelType은 "기타", channel 앞에 "[기타] "를 붙이고 videoId는 저장하지 않고 photo만 저장합니다.
  const handleAddActivity = async (activity) => {
    if (!uid) {
      alert("로그인이 필요합니다.");
      return;
    }
    try {
      const activityRef = doc(db, "users_data", uid, "activity", activity.id);
      const activityData = {
        title: activity.title,
        date: activity.date.split("T")[0] || activity.date,
        description: activity.description || "",
        channel: `[기타] ${activity.channel}`,
        channelType: "기타",
        photo: activity.photo || "",
        link: activity.link || "",
        createdAt: new Date().toISOString(),
      };
      await setDoc(activityRef, activityData);
      alert(`"${activity.title}" 활동이 아카이브에 추가되었습니다.`);
    } catch (error) {
      console.error("❌ 활동 저장 실패:", error);
      alert("활동 추가 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className={styleClasses.addActivityPage}>
      <h1>활동 가져오기</h1>

      {/* KOPIS 활동 검색 섹션 */}
      <div className={styleClasses.searchBox}>
        <h2>공연예술통합전산망 공연 활동 검색</h2>
        <p>공연시작일, 공연종료일, 공연명을 입력하세요.</p>
        <p>예) 공연시작일: 20230108, 공연종료일: 20230112, 공연명: 사랑</p>
        <div className="formRow">
          <div className={styleClasses.inputField}>
            <input
              type="text"
              placeholder="공연시작일 (YYYYMMDD)"
              value={stdate}
              onChange={(e) => setStdate(e.target.value)}
            />
          </div>
        </div>
        <div className="formRow">
          <div className={styleClasses.inputField}>
            <input
              type="text"
              placeholder="공연종료일 (YYYYMMDD)"
              value={eddate}
              onChange={(e) => setEddate(e.target.value)}
            />
          </div>
        </div>
        <div className="formRow">
          <div className={styleClasses.inputField}>
            <input
              type="text"
              placeholder="공연명 입력"
              value={shprfnm}
              onChange={(e) => setShprfnm(e.target.value)}
            />
          </div>
        </div>
        <button onClick={fetchKopisActivities} disabled={loading}>
          {loading ? "검색 중..." : "KOPIS 활동 검색"}
        </button>
      </div>

      {/* KCISA 활동 검색 섹션 */}
      <div className={styleClasses.searchBox}>
        <h2>문화체육관광부 공연 활동 검색</h2>
        <p>공연 분류(연극, 뮤지컬, 오페라, 음악, 콘서트, 국악, 무용, 전시, 기타)와 공연명을 입력하세요.</p>
        <p>예) 공연 분류: 뮤지컬, 공연명: 사랑</p>
        <div className="formRow">
          <div className={styleClasses.inputField}>
            <input
              type="text"
              placeholder="공연 분류 입력 (예: 뮤지컬)"
              value={kcisaCategory}
              onChange={(e) => setKcisaCategory(e.target.value)}
            />
          </div>
        </div>
        <div className="formRow">
          <div className={styleClasses.inputField}>
            <input
              type="text"
              placeholder="공연명 입력"
              value={kcisaTitle}
              onChange={(e) => setKcisaTitle(e.target.value)}
            />
          </div>
        </div>
        <button onClick={fetchKcisaActivities} disabled={loading}>
          {loading ? "검색 중..." : "KCISA 활동 검색"}
        </button>
      </div>

      {/* 활동 목록 나열 (KOPIS과 KCISA 모두) */}
      <div className={styleClasses.activityList}>
        {(kopisActivities.length > 0 || kcisaActivities.length > 0) ? (
            <>
            {kopisActivities.map((activity) => (
                <div key={`kopis-${activity.id}`} className={styleClasses.activityCard}>
                <div className={styleClasses.activityHeader}>
                    <img src={activity.photo} alt="활동 썸네일" />
                    <p>제목 : {activity.title}</p>
                    <p>공연시설 : {activity.channel}</p>
                    <p>공연일자 : {activity.date}</p>
                </div>
                {activity.description && (
                    <div className={styleClasses.activityDescription}>
                    <p>{activity.description}</p>
                    </div>
                )}
                <button onClick={() => handleAddActivity(activity)}>
                    아카이브에 추가
                </button>
                </div>
            ))}
            {kcisaActivities.map((activity) => (
                <div key={`kcisa-${activity.id}`} className={styleClasses.activityCard}>
                <div className={styleClasses.activityHeader}>
                    <img src={activity.photo} alt="활동 썸네일" />
                    <p>제목 : {activity.title}</p>
                    <p>공연시설 : {activity.channel}</p>
                    <p>공연일자 : {activity.date}</p>
                </div>
                {activity.description && (
                    <div className={styleClasses.activityDescription}>
                    <p>{activity.description}</p>
                    </div>
                )}
                <button onClick={() => handleAddActivity(activity)}>
                    아카이브에 추가
                </button>
                </div>
            ))}
            </>
        ) : (
            <p>검색 결과가 없습니다.</p>
        )}
      </div>
    </div>
  );
}

export default ImportStages;